import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmStatusModel } from '../../models/CRM';

/** Contadores de pedidos por crm_status_id */
export type CRMStatusCounts = Record<string, number>;

export interface CRMStatusToolbarProps {
  /** Lista de estados CRM disponibles */
  statusList: CrmStatusModel[];
  /** Callback cuando cambia el estado seleccionado */
  onStatusChange?: (statusId?: number | null) => void;
  /** Contadores de pedidos por estado */
  statusCounts?: CRMStatusCounts;
  /** Total de pedidos del GET normal (para "Todos") */
  total?: number;
  /** Si está cargando los contadores */
  isLoadingCounts?: boolean;
  /** Estado activo actual */
  active: string;
  /** Función para cambiar el estado activo */
  setActive: (key: string) => void;
}

export const CRMStatusToolbar = ({
  statusList,
  onStatusChange,
  statusCounts = {},
  total = 0,
  isLoadingCounts = false,
  active,
  setActive,
}: CRMStatusToolbarProps) => {
  const { t } = useTranslation('crmModule');

  // Obtener el conteo para un crm_status_id específico
  const getCountForStatus = (statusId?: number | null): number | undefined => {
    if (statusId === undefined) {
      // "Todos" - usar el total del GET normal
      return total;
    }
    if (statusId === null) {
      // "Sin estado" - usar la clave especial del conteo
      return statusCounts['null'];
    }
    return statusCounts[String(statusId)];
  };

  // Formatear el conteo para mostrar
  const formatCount = (count: number | undefined): string => {
    if (count === undefined) return '';
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return String(count);
  };

  // Construir botones: "Todos" + "Sin estado" + cada estado CRM
  const toolbarButtons = useMemo(() => {
    const buttons = [
      { key: 'all', label: t('toolbar.all', 'Todos'), statusId: undefined },
      { key: 'no-status', label: t('toolbar.noStatus', 'Sin estado'), statusId: null },
    ];

    // Ordenar estados: editables primero, fijos al final
    const sortedStatusList = [...statusList].sort((a, b) => {
      const aHasTeamId = a.team_id != null;
      const bHasTeamId = b.team_id != null;
      if (aHasTeamId && !bHasTeamId) return -1;
      if (!aHasTeamId && bHasTeamId) return 1;
      return 0;
    });

    // Agregar cada estado CRM ordenado
    sortedStatusList.forEach((status) => {
      buttons.push({
        key: `status_${status.id}`,
        label: status.name,
        statusId: status.id,
      });
    });

    return buttons;
  }, [statusList, t]);

  return (
    <div
      className="flex bg-card-bg rounded-lg text-sm p-1 mb-4 overflow-x-auto scrollbar-hide light:border-card-border light:border"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {toolbarButtons.map((btn) => {
        const count = getCountForStatus(btn.statusId);
        const countText = formatCount(count);

        return (
          <a
            key={btn.key}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setActive(btn.key);
              if (onStatusChange) {
                onStatusChange(btn.statusId);
              }
            }}
            className={`px-3 py-1 rounded hover:bg-hover-press whitespace-nowrap flex items-center gap-1.5 ${active === btn.key ? 'bg-hover-press' : ''}`}
          >
            <span>{btn.label}</span>
            {countText && (
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
                  active === btn.key
                    ? 'bg-primary/20 text-primary'
                    : 'bg-muted text-muted-foreground'
                } ${isLoadingCounts ? 'animate-pulse' : ''}`}
              >
                {countText}
              </span>
            )}
          </a>
        );
      })}
    </div>
  );
};

